<template>
  <v-card elevation="0" outlined min-height="350" class="d-flex flex-column h-100">
    <v-card-title class="py-1 white--text d-flex align-center justify-space-between" style="flex-wrap: nowrap;"
      :class="$vuetify.theme.dark ? 'black' : 'primary'">
      <div class="d-flex align-center" style="flex: 1 1 auto; min-width: 0;">
        <v-icon color="white" class="mr-2" medium>skip_next</v-icon>
        <span
          class="white--text body-font body-1 font-weight-bold text-uppercase text-ellipsis">{{ $t("dashboards.nextMatchdayGames") }}</span>
      </div>
      <v-btn v-if="!loading && games && games[0]" class="pl-0 pr-0" text small :to="{
        path: '/leagues',
        query: {
          leagueId: games[0].leagueId,
          tab: 'matchdays',
          matchday: games[0].matchDay
        }
      }" style="flex-shrink: 0;">
        <span class="white--text text-decoration-underline font-weight-bold">{{ $t('general.seeAll') }}</span>
        <v-icon small class="ml-1" color="white">keyboard_arrow_right</v-icon>
      </v-btn>
    </v-card-title>

    <v-card-text class="d-flex flex-column flex-grow-1 pa-0">
      <template v-if="!loading">
        <div v-if="!games || !games[0]"
          class="d-flex justify-center align-center flex-column px-3 py-4 mb-13 text-center flex-grow-1">
          <v-icon color="primary lighten-5"
            v-bind:style="{ 'font-size': $vuetify.breakpoint.mdAndUp ? '80px' : '60px' }">event_busy</v-icon>
          <span class="primary--text lighten--text-2 body-1">{{ $t("dashboards.placeholderNextMatchDayNotFound") }}</span>
        </div>

        <template v-else>
          <div class="mt-1 pl-6 pr-4 pt-1 pb-0 d-flex align-center justify-space-between">
            <div class="d-flex align-center">
              <img
                :src="`https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/leagues/${games[0].leagueId}.png`"
                height="18" width="18" class="mr-1" @error="onLeagueImgError" />
              <span class="subheader-font body-2 primary--text font-weight-bold"
                style="opacity: 1 !important;">{{ games[0].leagueName }}</span>
            </div>
            <span class="subheader-font body-2 primary--text font-weight-bold" style="opacity: 1 !important;"
              v-if="games[0].matchDay">Matchday {{ games[0].matchDay }}</span>
          </div>

          <div class="pr-3 pb-2 pt-1">
            <div v-for="(game, index) in games" :key="index" class="body-2 pl-6 my-1 d-flex align-center">
              <div class="w-11 text-center" v-if="$vuetify.breakpoint.mdAndUp" style="white-space: nowrap;">
                <span class="primary--text body-1" style="white-space: nowrap;">{{ getLocalMatchDate(game.matchDate,
                  "DD/MM/YYYY") }}</span>
              </div>
              <router-link class="w-35 text-right d-flex align-center justify-end pr-1 primary--text body-1 mw-0"
                :to="`/team/${game.homeTeamId}`">
                <div>
                  <v-img
                    :src="game.homeTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.homeTeamId + '.png'"
                    :height="18" :width="18" class="mx-1" v-on:error="onImgError($event, game, 'home')" />
                </div>
                <span>{{ game.homeTeamName }}</span>
              </router-link>
              <div class="text-center font-weight-bold"
                v-bind:class="{ 'w-8': $vuetify.breakpoint.mdAndUp, 'w-15': !$vuetify.breakpoint.mdAndUp }">
                <span></span>
                <span class="mx-1">-</span>
                <span></span>
              </div>
              <router-link class="w-35 d-flex align-center pl-1 primary--text body-1 mw-0"
                :to="`/team/${game.awayTeamId}`">
                <span>{{ game.awayTeamName }}</span>
                <div>
                  <v-img
                    :src="game.awayTeamImgError ? 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/0.png' : 'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + game.awayTeamId + '.png'"
                    :height="18" :width="18" class="mx-1" v-on:error="onImgError($event, game, 'away')" />
                </div>
              </router-link>
              <div class="flex-shrink-0" style="width: max-content;">
                <v-btn block color="primary" :to="`/game/${game.gameId}`" rounded outlined
                  class="elevation-0 monospace-font" x-small>
                  <div class="d-flex align-center" v-if="game.statusId !== 4">
                    {{ $vuetify.breakpoint.mdAndUp ? $t("gameAnalysis.seeDetail") : '' }}
                    <v-icon v-if="!$vuetify.breakpoint.mdAndUp">chevron_right</v-icon>
                  </div>
                  <div v-if="game.statusId === 4" class="d-flex align-center">
                    <v-icon color="red" class="mr-1 animate-flicker" small>adjust</v-icon>
                    <span v-if="$vuetify.breakpoint.mdAndUp">{{ $t("general.live") }}</span>
                  </div>
                </v-btn>
              </div>
            </div>
          </div>
        </template>

      </template>

      <template v-else>
        <div class="mt-4 pl-6 pr-4 d-flex align-center justify-space-between">
          <v-skeleton-loader type="text" width="30%" class="ma-0"></v-skeleton-loader>
          <v-skeleton-loader type="text" width="15%" class="ma-0"></v-skeleton-loader>
        </div>
        <div class="px-6 py-4 flex-grow-1 d-flex flex-column justify-space-around">
          <v-skeleton-loader v-for="i in 5" :key="i" type="text" width="100%" class="my-2"></v-skeleton-loader>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import GameService from "../../../services/game/game.service";
import ResizeObserver from 'resize-observer-polyfill';
import { getLocalMatchDate } from "../../../utils/functions";

export default {
  name: "NextMatchdayGamesWidget",
  components: {
  },
  props: {
    seasonId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      games: null,
      loading: false,
      observer: null
    };
  },
  filters: {
  },
  mounted() {
    this.loadViewData();
    this.observer = new ResizeObserver(this.onResize)
      .observe(this.$el)
  },
  methods: {
    async loadViewData() {
      await this.loadGames();
    },
    async loadGames() {
      this.loading = true;
      let gameResponse = await GameService.getNextMatchdayGames(
        this.seasonId
      );
      this.games = _.reverse(gameResponse.data);
      this.loading = false;
    },
    onImgError(event, item, side) {
      if (side === "home") {
        item.homeTeamImgError = true;
      } else if (side === "away") {
        item.awayTeamImgError = true;
      }
    },
    onLeagueImgError(event) {
      event.target.style.display = 'none';
    },
    onResize() {
      this.$emit('heightChange', this.$el.clientHeight)
    },
    getLocalMatchDate(date, format) {
      return getLocalMatchDate(date, format);
    },
  },
  destroyed() {
    delete this.observer
  }
};
</script>


// WEBPACK FOOTER //
// src/views/dashboard/widgets/NextMatchdayGamesWidget.vue